import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

import { db } from "./firebase";
import { computeScheduledAt } from "../utils/timezoneScheduling";

const COLLECTION = "scheduledReports";

// ============================================================
// ONE-TIME SCHEDULING
// ============================================================
// A schedule fires exactly once, at `scheduledAt`, then moves to a
// terminal status ("sent" or "failed") and is never processed again —
// there is no recurrence and no `nextRunAt` recalculation. See
// IMPLEMENTATION_ONE_TIME_SCHEDULING.md for the full rationale and the
// migration story for schedules created under the old recurring model.

// =======================================
// CREATE SCHEDULE
// =======================================
// `uid` and `sessionId` are taken as explicit, separate arguments —
// deliberately not read off a generic spread object — so there is exactly
// one place a caller can get this wrong, and it's an obvious one: pass
// the signed-in user's real `user.uid` and the currently selected
// session's real Firestore document id (`session.firebaseId`). Nothing
// here ever fabricates, defaults, or randomly generates either value —
// if the caller doesn't have both, this throws instead of silently
// writing a bad document.
//
// `formData` is the schedule form's remaining plain-object shape (see
// ScheduleReportModal): { reportName, date, time, timezone, recipients,
// recipientListIds, includeMetrics, includeAiSummary, includeDashboardLink }
export async function createScheduledReport(uid, sessionId, formData) {
  if (!uid) {
    throw new Error("A signed-in user is required to schedule a report.");
  }

  if (!sessionId) {
    throw new Error("A saved session is required to schedule a report — open or select a dataset first.");
  }

  const scheduledAtDate = computeScheduledAt(formData);

  if (!scheduledAtDate) {
    throw new Error("A valid date and time are required to schedule a report.");
  }

  const docRef = await addDoc(collection(db, COLLECTION), {
    // Required by the backend — see functions/index.js.
    userId: uid,
    sessionId: sessionId,
    status: "scheduled",

    reportName: formData.reportName,
    recipients: formData.recipients || [],
    scheduledAt: Timestamp.fromDate(scheduledAtDate),

    includeAiSummary: formData.includeAiSummary,
    includeDashboardLink: formData.includeDashboardLink,
    includeMetrics: formData.includeMetrics,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),

    processingStartedAt: null,
    sentAt: null,
    lastError: null,

    // Preserved beyond the backend's minimum required fields — existing
    // recipient-list support (the backend's resolveScheduleRecipients
    // already merges these in with `recipients`) and the display time
    // zone shown in the schedule list/edit form.
    recipientListIds: formData.recipientListIds || [],
    timezone: formData.timezone,
  });

  return docRef.id;
}

// =======================================
// LOAD USER'S SCHEDULES
// =======================================
export async function getUserScheduledReports(uid) {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", uid),
    orderBy("createdAt", "desc")
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
}

// =======================================
// UPDATE SCHEDULE (only while status is "scheduled")
// =======================================
// Editing a report that's already sent, failed, or mid-send doesn't make
// sense — the modal only offers Edit for "scheduled" items, but this is
// enforced here too so a stale UI can't accidentally rewrite a report
// that already went out.
export async function updateScheduledReport(id, updates) {
  const payload = { ...updates, updatedAt: serverTimestamp() };

  // Recompute scheduledAt whenever the date/time/timezone changed, so the
  // stored send time immediately reflects what's shown in the UI.
  if (updates.date || updates.time || updates.timezone) {
    const scheduledAtDate = computeScheduledAt(updates);
    if (scheduledAtDate) {
      payload.scheduledAt = Timestamp.fromDate(scheduledAtDate);
    }
  }

  await updateDoc(doc(db, COLLECTION, id), payload);
}

// =======================================
// CANCEL SCHEDULE
// =======================================
// A one-time schedule that hasn't fired yet can be cancelled outright
// (there's no "pause and resume" for a single send — it either hasn't
// gone out, or it has). Implemented as a status change rather than a
// delete so the record stays visible for history; use deleteScheduledReport
// if the user wants it removed entirely.
export async function cancelScheduledReport(id) {
  await updateDoc(doc(db, COLLECTION, id), {
    status: "cancelled",
    updatedAt: serverTimestamp(),
  });
}

// =======================================
// DELETE SCHEDULE
// =======================================
export async function deleteScheduledReport(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}
